import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';

interface ForeignEvent { event_id: string; object_id: string; family: string; event_type: string;
  amount_cents: number | null; currency: string | null; received_at: string; reason: string }
export function StripeForeignEventsPage() {
  const [page, setPage] = useState(1), [rows, setRows] = useState<ForeignEvent[]>([]);
  const [total, setTotal] = useState(0), [pages, setPages] = useState(0), [loading, setLoading] = useState(true), [error, setError] = useState('');
  useEffect(() => {
    let active = true; setLoading(true); setError('');
    void api.get('admin/stripe/foreign-events', { searchParams: { page, limit: 50 } })
      .json<{ data: { rows: ForeignEvent[]; total: number; total_pages: number } }>()
      .then(r => { if (active) { setRows(r.data.rows); setTotal(r.data.total); setPages(r.data.total_pages); } })
      .catch(() => { if (active) setError('Unable to load Stripe events. Try again.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [page]);
  const columns: Column<ForeignEvent>[] = [
    { key: 'event_id', header: 'Event ID', cell: row => <span style={{ overflowWrap: 'anywhere' }}>{row.event_id}</span> },
    { key: 'object_id', header: 'Object', cell: row => <span style={{ overflowWrap: 'anywhere' }}>{row.object_id}</span> },
    { key: 'family', header: 'Family', cell: row => row.event_type },
    { key: 'amount', header: 'Amount', cell: row => row.amount_cents === null || !row.currency ? 'Not provided'
      : new Intl.NumberFormat('en', { style: 'currency', currency: row.currency }).format(row.amount_cents / 100) },
    { key: 'received_at', header: 'Received (UTC)', cell: row => new Date(row.received_at).toISOString().replace('T', ' ').replace('.000Z', ' UTC') },
    { key: 'reason', header: 'Disposition', cell: row => `Foreign owner: ${row.reason}` },
  ];
  return <section style={{ padding: '24px 16px', minWidth: 0, color: 'var(--color-tx)' }}>
    <h1>Foreign Stripe events</h1>
    <p>These events were acknowledged without changing EVI v3 records. Handle legacy refunds and disputes directly in Stripe.</p>
    <p>{total} events. This list is read only.</p>
    {error && <p role="alert">{error}</p>}
    {loading ? <p role="status">Loading events...</p> : <ResponsiveTable columns={columns} rows={rows} rowKey={row => row.event_id} emptyMessage="No foreign-owner events recorded." />}
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginTop: 16 }}>
      <button disabled={loading || page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
      <span>Page {page} of {Math.max(1, pages)}</span>
      <button disabled={loading || page >= pages} onClick={() => setPage(p => p + 1)}>Next</button>
    </div>
  </section>;
}
